import {useEffect, useState} from 'react';

import {Device} from '../types/Breakpoint';

/**
 * Mobile-first breakpoints (min-width)
 * Matches Tailwind config breakpoints
 */
const BREAKPOINTS = {
  mobile: '(max-width: 767px)',
  tablet: '(min-width: 768px) and (max-width: 1024px)',
  notebook: '(min-width: 1025px) and (max-width: 1366px)',
  desktop: '(min-width: 1367px)',
  printer: 'print',
} as const;

/**
 * Hook to detect the current device/breakpoint
 * Returns the current device based on window width
 * Updates when window is resized
 * @returns Current device type
 * @example const device = useDevice(); // 'mobile' | 'tablet' | 'notebook' | 'desktop' | 'printer'
 */
export const useDevice = (): Device => {
  const [currentDevice, setCurrentDevice] = useState<Device>(() => getCurrentDevice());

  useEffect(() => {
    const mediaQueryLists = (Object.entries(BREAKPOINTS) as [Device, string][]).map(
      ([device, mediaQuery]) => ({
        device,
        queryList: window.matchMedia(mediaQuery),
        listener: (event: MediaQueryListEvent) => {
          if (event.matches) {
            setCurrentDevice(device);
          }
        },
      })
    );

    mediaQueryLists.forEach((deviceQueryList) => {
      deviceQueryList.queryList.addEventListener('change', deviceQueryList.listener);
    });

    return () => {
      mediaQueryLists.forEach((deviceQueryList) => {
        deviceQueryList.queryList.removeEventListener('change', deviceQueryList.listener);
      });
    };
  }, []);

  return currentDevice;
};

/**
 * Get current device based on window.matchMedia
 */
const getCurrentDevice = (): Device => {
  if (typeof window === 'undefined') {
    return 'desktop';
  }

  const mediaQueryLists = (Object.entries(BREAKPOINTS) as [Device, string][]).map(
    ([device, mediaQuery]) => ({
      device,
      queryList: window.matchMedia(mediaQuery),
    })
  );

  const matched = mediaQueryLists.find((item) => item.queryList.matches);
  return matched?.device ?? 'desktop';
};
